import { useMemo } from "react";
import { DOMAIN } from "@/hooks/useCapabilities";
import useCapabilities from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { MaterialNames } from "@/components/material_symbol_names";
import { ROLE } from "@/providers/AuthorizationProvider/types";

export const getUniversityNavLinks = (
  universityId: string,
  options?: { includeReports?: boolean; includeStaff?: boolean }
) => {
  const { includeReports = true, includeStaff = false } = options ?? {};

  return [
    {
      label: "Students",
      link: { to: "/education/universities/:universityId/students" as const, params: { universityId } },
      icon: "groups" as MaterialNames,
      domain: DOMAIN.UNIVERSITIES
    },
    {
      label: "Events",
      link: { to: "/education/universities/:universityId/events" as const, params: { universityId } },
      icon: "event" as MaterialNames,
      domain: DOMAIN.UNIVERSITIES
    },
    {
      label: "Internships",
      link: { to: "/education/universities/:universityId/internships" as const, params: { universityId } },
      icon: "business_center" as MaterialNames,
      domain: DOMAIN.UNIVERSITIES
    },
    {
      label: "Test Results",
      link: { to: "/education/universities/:universityId/test-results" as const, params: { universityId } },
      icon: "inventory" as MaterialNames,
      domain: DOMAIN.UNIVERSITIES
    },
    ...(includeReports
      ? [
          {
            label: "Reports",
            link: { to: "/education/universities/:universityId/reports" as const, params: { universityId } },
            icon: "description" as MaterialNames,
            domain: DOMAIN.UNIVERSITIES
          }
        ]
      : []),
    ...(includeStaff
      ? [
          {
            label: "Staff",
            link: { to: "/education/universities/:universityId/staff" as const, params: { universityId } },
            icon: "badge" as MaterialNames,
            domain: DOMAIN.UNIVERSITIES
          }
        ]
      : [])
  ];
};

export function useUniversityNavLinks(universityId: string) {
  const flags = useFlags();
  const { hasRole } = useCapabilities();
  const canManageUniversityStaff = hasRole(ROLE.EDUCATION_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN);

  return useMemo(
    () =>
      getUniversityNavLinks(universityId, {
        includeReports: !!flags["backoffice-university-reports"],
        includeStaff: canManageUniversityStaff
      }),
    [universityId, flags, canManageUniversityStaff]
  );
}
